import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from './../../environments/environment';
import { User } from './../models/user';
import { Species } from '../models/species';
import { Disease } from '../models/disease';
import { SurgicalOperation } from '../models/surgicalOperation';
import { PharmacyProduct, PharmacyProductUnit } from './../models/pharmacy';
import { StorageService } from './storage.service'
import { AlertController } from '@ionic/angular';
import { NetworkService } from './network.service';
import { UserService } from './user.service';
import { DateUtilsService } from './date-utils.service';
import { DiseaseService } from './disease.service';
import { SurgicalOperationService } from './surgical-operation.service';
import { IndexedDbService } from './indexed-db.service';
import { blobToBase64 } from '../utils'
import { TranslateService } from '@ngx-translate/core';
import { ComptaInvoice } from '../models/compta';

@Injectable({
  providedIn: 'root'
})
export class PharmacyService {
  pharmacyProducts: PharmacyProduct[] = [];
  pharmacyProductUnits: PharmacyProductUnit[] = [];

  constructor(
    private http: HttpClient,
    public storageService: StorageService,
    public networkService: NetworkService,
    private translate: TranslateService,
    private dateUtils: DateUtilsService,
    private diseaseService: DiseaseService,
    private surgicalOperationService: SurgicalOperationService,
    private userService: UserService,
    public indexedDbService: IndexedDbService,
    public alertController: AlertController) { }

    dbSavePharmacyProducts(pharmacyProducts: PharmacyProduct[]): Promise<PharmacyProduct[]> {
      return new Promise((resolve, reject) => {
        if (this.networkService.isOnline()) {
          let headers = new HttpHeaders({
              'Authorization': this.userService.getHttpHeaders()
          });
          let formData: { [key: string]: any } = {};

          formData["products"] = [];
          for(let pharmacyProduct of pharmacyProducts) {
            formData["products"].push({
              'id': pharmacyProduct.id,
              'quantity': pharmacyProduct.quantity,
              'unit': pharmacyProduct.unit?.id,
              'name': pharmacyProduct.name,
              'eventId': pharmacyProduct.eventId,
              'userId': pharmacyProduct.userId,
              'photoBase64': pharmacyProduct.photoBase64,
            })
          }
    
          this.http.post<any[]>(environment.API_URL + 'pharmacy/ws_private/UpdateProducts/', formData, {headers : headers}).subscribe({
            next: (response: { [key: string]: any }) => {
              for(let i = 0; i < response['products'].length; i++) {
                let pharmacyProduct = pharmacyProducts[i]
                if(pharmacyProduct.id == null) {
                  pharmacyProduct.id = response['products'][i]['id']
                  this.pharmacyProducts.push(pharmacyProduct);
                }
                pharmacyProduct.description = response['products'][i]['description']
              }
              resolve(pharmacyProducts);
            },
            error: async (error) => {
              reject(error);
            }
          });
        } else {
          this.indexedDbService.addItem({'obj': pharmacyProducts, 'action': 'save'}).subscribe(() => {});
          resolve(pharmacyProducts)
        }
      });
    }

  dbSavePharmacyProduct(pharmacyProduct: PharmacyProduct) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = pharmacyProduct.id;

        formData["quantity"] = pharmacyProduct.quantity;
        formData["unit"] = pharmacyProduct.unit?.id;
        formData["name"] = pharmacyProduct.name;
        formData["eventId"] = pharmacyProduct.eventId;
        formData["userId"] = pharmacyProduct.userId;
        formData["photoBase64"] = pharmacyProduct.photoBase64;

        this.http.post<any[]>(environment.API_URL + 'pharmacy/ws_private/UpdateProduct/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            if (pharmacyProduct.id == null) {
              pharmacyProduct.id = response['id']
              this.pharmacyProducts.push(pharmacyProduct);
            }
            pharmacyProduct.description = response['description']
            resolve(pharmacyProduct);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': pharmacyProduct, 'action': 'save'}).subscribe(() => {});
        resolve(pharmacyProduct)
      }
    });
  }

  dbGetPharmacyProduct(comptaService: any, pharmacyProductId: number | null): Promise<PharmacyProduct | null | undefined> {
    return new Promise((resolve) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });

        var url = environment.API_URL + 'pharmacy/ws_private/' + pharmacyProductId + '/GetProduct/';
        if(pharmacyProductId == null)
          url = environment.API_URL + 'pharmacy/ws_private/GetProductBlank/';

        this.http.get<any[]>(url, {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          for(let s of response['unitsList']) {
            var d = this.pharmacyProductUnits.find(a => a.id == s['id'])
            if(!d){
              d = new PharmacyProductUnit()
              d.id = s['id'];
              this.pharmacyProductUnits.push(d);
            }
            d.name = s['name'];
          }
          
          var pharmacyProduct = this.pharmacyProducts.find(a => a.id == pharmacyProductId)
          if(!pharmacyProduct){
            pharmacyProduct = new PharmacyProduct()
            this.pharmacyProducts.push(pharmacyProduct);
          }

          pharmacyProduct.id = response['id']
          pharmacyProduct.userId = response['userId']
          pharmacyProduct.name = response['name']
          pharmacyProduct.description = response['description']
          pharmacyProduct.quantity = response['quantity']
          pharmacyProduct.date = response['date']
          pharmacyProduct.photoBase64 = response['photoBase64']
          pharmacyProduct.unit = this.pharmacyProductUnits.find(a => a.id == response['unit']['id']) ?? null;

          pharmacyProduct.invoices = []
          for(var invoice of response['invoices']) {
            var i = comptaService.invoices.find((b: ComptaInvoice) => b.id == invoice['id'])
            if(!i){
              i = new ComptaInvoice()
              comptaService.invoices.push(i)
            }
            i.id = pharmacyProduct['id']
            i.date = pharmacyProduct['date']
            pharmacyProduct.invoices.push(i)
          }

          resolve(pharmacyProduct);
        },
        () => {
          resolve(this.pharmacyProducts.find(a => a.id == pharmacyProductId));
        })
      } else {
        resolve(this.pharmacyProducts.find(a => a.id == pharmacyProductId));
      }
    });
  }

  dbGetPharmacyProductsOfUser(comptaService: any, userId: number): Promise<PharmacyProduct[] | null> {
    return new Promise((resolve) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        this.http.get<any[]>(environment.API_URL + 'pharmacy/ws_private/' + userId + '/GetProductsOfUser/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          for(let s of response['unitsList']) {
            var d = this.pharmacyProductUnits.find(a => a.id == s['id'])
            if(!d){
              d = new PharmacyProductUnit()
              d.id = s['id'];
              this.pharmacyProductUnits.push(d);
            }
            d.name = s['name'];
          }

          var pharmacyProducts: PharmacyProduct[] =[]
          for(let b of response['products']) {
            var pharmacyProduct = this.pharmacyProducts.find(a => a.id == b['id'])
            if(!pharmacyProduct){
              pharmacyProduct = new PharmacyProduct()
              this.pharmacyProducts.push(pharmacyProduct);
            }
            pharmacyProduct.id = b['id']
            pharmacyProduct.userId = b['userId']
            pharmacyProduct.name = b['name']
            pharmacyProduct.description = b['description']
            pharmacyProduct.quantity = b['quantity']
            pharmacyProduct.date = b['date']
            pharmacyProduct.photoBase64 = b['photoBase64']
            pharmacyProduct.unit = this.pharmacyProductUnits.find(a => a.id == b['unit']['id']) ?? null;

            pharmacyProduct.invoices = []
            for(var invoice of b['invoices']) {
              var i = comptaService.invoices.find((b: ComptaInvoice) => b.id == invoice['id'])
              if(!i){
                i = new ComptaInvoice()
                comptaService.invoices.push(i)
              }
              i.id = pharmacyProduct['id']
              i.date = pharmacyProduct['date']
              pharmacyProduct.invoices.push(i)
            }

            pharmacyProducts.push(pharmacyProduct);
          }

          resolve(pharmacyProducts);
        },
        () => {
          resolve(this.pharmacyProducts.filter(a => a.userId = userId));
        })
      } else {
        resolve(this.pharmacyProducts.filter(a => a.userId = userId));
      }
    });
  }

  dbDeletePharmacyProduct(pharmacyProduct: PharmacyProduct) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = pharmacyProduct.id;

        this.pharmacyProducts = this.pharmacyProducts.filter(a => a.id != pharmacyProduct.id);
        
        this.http.post<any[]>(environment.API_URL + 'pharmacy/ws_private/DeleteProduct/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            resolve(pharmacyProduct);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': pharmacyProduct, 'action': 'delete'}).subscribe(() => {});
        resolve(pharmacyProduct)
      }
    });
  }
}
